import React, { useState } from "react";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { ModuleInputType, ModuleProxy } from "@me-team/host/main/graphql/types";
import Form from "react-bootstrap/Form";
import default_module_img from "@me-team/host/src/images/default_module_img.png";
import { useTranslation } from "react-i18next";
import styles from "../ModulesListStyles.module.scss";
import { ModulesTagName, SubModulesTagName } from "../../../../../utils/enums";
import { useParams } from "react-router-dom";
import { DisableModuleDataInterface } from "../../../../../interfaces/interfaces";
import ModulesService from "../../../../../services/ModulesService/ModulesService";

type ServicesModuleProps = {
    modulesList: ModuleProxy[];
    updateModules: (
        companyId: number,
        updates: ModuleInputType[],
        branchId: number,
        isNeededToDisable?: boolean
    ) => Promise<void>;
    openDisableModal: (moduleData: DisableModuleDataInterface) => void;
    canManageModules: boolean;
};

const ServicesModule: React.FC<ServicesModuleProps> = (props) => {
    const { currentCompanyId, branchId } = useParams();
    const { t: translate } = useTranslation();
    const SERVICES_MODULE: ModuleProxy = props.modulesList.find(
        (module: ModuleProxy) => module.name === ModulesTagName.SERVICES
    );
    const basePath: string = process.env.REACT_APP_POS_URL;
    const imageSRC: string = SERVICES_MODULE.image
        ? `${basePath}/build/images/modules/${SERVICES_MODULE.image}`
        : default_module_img;
    const PERSONAL_MODULE: ModuleProxy = props.modulesList.find(
        (module: ModuleProxy) => module.name === ModulesTagName.PERSONNEL
    );
    const [isMainSwitchDisable, setIsMainSwitchDisable] = useState(false);

    const onToggleServicesModule = async () => {
        if (!props.canManageModules) return;

        setIsMainSwitchDisable(true);

        const updates: ModuleInputType[] = [{ id: SERVICES_MODULE.id, isActive: !SERVICES_MODULE.active }];

        if (SERVICES_MODULE.active) {
            const disabledData: DisableModuleDataInterface = ModulesService.disabledModule(
                SERVICES_MODULE,
                props.modulesList
            );

            if (disabledData.dependentModules.length !== 0) {
                props.openDisableModal(disabledData);
                setIsMainSwitchDisable(false);
                return;
            }
        } else {
            if (PERSONAL_MODULE.active) {
                const serviceProvides = ModulesService.getChildModule(
                    SERVICES_MODULE,
                    SubModulesTagName.SERVICE_PROVIDES
                );
                updates.push({ id: serviceProvides.id, isActive: true });
            }
        }

        await props.updateModules(Number(currentCompanyId), updates, Number(branchId), true);
        setIsMainSwitchDisable(false);
    };

    return (
        <div className={"border rounded p-4 mt-4"}>
            <Row>
                <Col xl={2} xxl={2} className="mb-3 mb-xl-0">
                    <div className={!SERVICES_MODULE.active ? "opacity-75" : ""}>
                        <img
                            className={`${styles.customImagesSize} d-block`}
                            src={imageSRC}
                            alt={SERVICES_MODULE.name}
                        />
                    </div>
                </Col>
                <Col xl={5} xxl={6} className="mb-3 mb-xl-0">
                    <div className={!SERVICES_MODULE.active ? "opacity-75" : ""}>
                        <h5 className={"fw-normal mb-2 fs-24"}>{translate(SERVICES_MODULE.name)}</h5>
                        <p className={"mb-0 text-grey"}>{SERVICES_MODULE.description}</p>
                        {SERVICES_MODULE.descriptionInfo && (
                            <p className={"mb-0 text-grey mt-2"}>
                                <i className="bi bi-info-circle me-2"></i>
                                {SERVICES_MODULE.descriptionInfo}
                            </p>
                        )}
                    </div>
                </Col>
                <Col
                    xs={12}
                    sm={6}
                    md={6}
                    xl={2}
                    xxl={2}
                    className={"d-flex justify-content-start justify-content-sm-center d-xl-block mb-3 mb-sm-0"}
                >
                    <Form className={!SERVICES_MODULE.active ? "opacity-75" : ""}>
                        <ul className="list-unstyled">
                            {SERVICES_MODULE.childModules.map((childModule: ModuleProxy, index: number) => (
                                <li
                                    className={`d-flex align-items-center ${
                                        index === SERVICES_MODULE.childModules.length - 1 ? "" : "mb-3"
                                    }`}
                                    key={childModule.id}
                                >
                                    <Form.Check
                                        type="switch"
                                        label={translate(childModule.name)}
                                        checked={childModule.active}
                                        disabled={true}
                                        className="mb-0 custom-module-switch"
                                        onChange={() => {}}
                                    />
                                    {childModule.name === SubModulesTagName.SERVICE_PROVIDES &&
                                        !PERSONAL_MODULE.active && (
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip id={`tooltip-${childModule.id}`}>
                                                        {translate(
                                                            'It will be enabled automatically when the \"Personnel\" module is connected.'
                                                        )}
                                                    </Tooltip>
                                                }
                                            >
                                                <i className="bi bi-question-circle ms-2 text-grey cursor-pointer"></i>
                                            </OverlayTrigger>
                                        )}
                                </li>
                            ))}
                        </ul>
                    </Form>
                </Col>
                <Col
                    xs={12}
                    sm={6}
                    md={6}
                    xl={3}
                    xxl={2}
                    className={"d-flex flex-column justify-content-between align-items-center align-items-xl-end"}
                >
                    <Form className={"d-flex align-items-center"}>
                        <Form.Check
                            type="switch"
                            label={SERVICES_MODULE.active ? translate("Enabled") : translate("Disabled")}
                            checked={SERVICES_MODULE.active}
                            disabled={isMainSwitchDisable || !props.canManageModules}
                            className="mb-0 custom-module-switch fs-6"
                            onChange={onToggleServicesModule}
                        />
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip id={`tooltip-${SERVICES_MODULE.id}`}>
                                    {translate(
                                        "You can disable/enable the module functionality at any time without canceling your subscription."
                                    )}
                                </Tooltip>
                            }
                        >
                            <i className="bi bi-question-circle ms-2 text-grey cursor-pointer"></i>
                        </OverlayTrigger>
                    </Form>
                    <h5 className={"mt-3 mt-sm-0"}>{translate("Free")}</h5>
                </Col>
            </Row>
        </div>
    );
};

export default React.memo(ServicesModule);
